import React, { createContext, useContext, useState, useEffect } from "react";
import { collection, doc, setDoc, deleteDoc, onSnapshot, query, getDoc } from "firebase/firestore";
import { db as firestoreDb } from "./firebase";
import { useAuth } from "./AuthContext";
import { CartItem, Product } from "../types";
import { toast } from "sonner";

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  loading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setCart([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const cartRef = collection(firestoreDb, "users", user.uid, "cart");
    const unsubscribe = onSnapshot(cartRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ ...doc.data() } as CartItem));
      setCart(items);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to cart:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const addToCart = async (product: Product) => {
    if (!user) {
      toast.error("Debes iniciar sesión para agregar al carrito");
      return;
    }

    try {
      const itemRef = doc(firestoreDb, "users", user.uid, "cart", product.id);
      const itemSnap = await getDoc(itemRef);
      
      if (itemSnap.exists()) {
        const currentData = itemSnap.data() as CartItem;
        await setDoc(itemRef, {
          ...currentData,
          quantity: currentData.quantity + 1
        }, { merge: true });
      } else {
        const newItem: CartItem = {
          productId: product.id,
          shopId: product.shopId,
          name: product.name,
          price: product.price,
          quantity: 1,
          image: product.images[0] || "",
          stock: product.stock
        };
        await setDoc(itemRef, newItem);
      }
      toast.success("Producto agregado al carrito");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Error al agregar al carrito");
    }
  };

  const removeFromCart = async (productId: string) => {
    if (!user) return;
    try {
      await deleteDoc(doc(firestoreDb, "users", user.uid, "cart", productId));
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const updateQuantity = async (productId: string, quantity: number) => {
    if (!user || quantity < 1) return;
    try {
      const itemRef = doc(firestoreDb, "users", user.uid, "cart", productId);
      await setDoc(itemRef, { quantity }, { merge: true });
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const clearCart = async () => {
    if (!user) return;
    try {
      // For simplicity, we'll just delete the items we have in state
      // In a real app, a batch delete or server-side function is better
      const promises = cart.map(item => deleteDoc(doc(firestoreDb, "users", user.uid, "cart", item.productId)));
      await Promise.all(promises);
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, loading }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
